import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { useTelegram } from '@/shared/lib/telegram'
import type { Tea } from '@/entities/product'

// ============================================================
// Типы (соответствуют backend)
// ============================================================

type RecommendationSource = 'diary' | 'favorites' | 'orders' | 'views' | 'popular'
type RecommendationSlotType = 'personalized' | 'discovery' | 'business' | 'new' | 'popular'

interface RecommendedProduct {
  id: string
  name: string
  slug: string
  categoryId: string
  categoryName: string
  subcategoryId: string | null
  subcategoryName: string | null
  priceAmount: number
  salePriceAmount: number | null
  isOnSale: boolean
  isNew: boolean
  isInStock: boolean
  imageUrl: string | null
  reasons: string[]
  slotType: RecommendationSlotType
}

interface RecommendationMeta {
  isPersonalized: boolean
  sources: RecommendationSource[]
  title: string
  subtitle: string
  slots: Record<RecommendationSlotType, number>
}

interface RecommendationsResponse {
  data: RecommendedProduct[]
  meta: RecommendationMeta
}

// ============================================================
// Хук для получения рекомендаций
// ============================================================

/**
 * Хук для получения персонализированных рекомендаций с бэкенда
 * 
 * Алгоритм на сервере учитывает:
 * - Записи дневника (особенно с высокими оценками)
 * - Избранное
 * - Историю заказов
 * - Популярность товаров
 */
export function useRecommendations(limit: number = 8) {
  const { initData } = useTelegram()

  const query = useQuery({
    queryKey: ['recommendations', limit],
    queryFn: async () => {
      const response = await apiClient<RecommendationsResponse>(
        `/api/user/recommendations?limit=${limit}`,
        { initData }
      )
      return response
    },
    enabled: !!initData,
    staleTime: 5 * 60 * 1000, // 5 минут — рекомендации не меняются часто
    refetchOnWindowFocus: false,
  })

  // Преобразуем RecommendedProduct в Tea для совместимости с компонентами
  const recommendations: Tea[] = (query.data?.data || []).map(product => ({
    id: product.id,
    name: product.name,
    slug: product.slug,
    categoryId: product.categoryId,
    categoryName: product.categoryName,
    subcategoryId: product.subcategoryId || undefined,
    subcategoryName: product.subcategoryName || undefined,
    priceAmount: product.priceAmount,
    priceCurrency: 'RUB',
    priceFormatted: `${product.priceAmount} ₽`,
    salePriceAmount: product.salePriceAmount || undefined,
    salePriceFormatted: product.salePriceAmount ? `${product.salePriceAmount} ₽` : undefined,
    isOnSale: product.isOnSale,
    isNew: product.isNew,
    isInStock: product.isInStock,
    isFeatured: false,
    images: product.imageUrl 
      ? [{ id: '1', url: product.imageUrl, alt: product.name, isPrimary: true, sortOrder: 0 }] 
      : [],
    // Дополнительные поля Tea
    description: undefined,
    shortDescription: undefined,
    originCountry: undefined,
    originRegion: undefined,
    harvestSeason: undefined,
    harvestYear: undefined,
    elevation: undefined,
    brewing: undefined,
    tasteProfile: undefined,
    sortOrder: 0,
    viewCount: 0,
    createdAt: '',
    updatedAt: '',
  }))

  return {
    recommendations,
    meta: query.data?.meta || {
      isPersonalized: false,
      sources: [] as RecommendationSource[],
      title: 'Популярное',
      subtitle: 'Чаи, которые выбирают чаще всего',
    },
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    // Для удобства — прямой доступ к полям meta
    isPersonalized: query.data?.meta?.isPersonalized || false,
    source: {
      isPersonalized: query.data?.meta?.isPersonalized || false,
      title: query.data?.meta?.title || 'Популярное',
      subtitle: query.data?.meta?.subtitle || 'Чаи, которые выбирают чаще всего',
    },
  }
}
